import React, { useMemo } from 'react';
import {
  computeAttackStrategyStatistics,
  getAttackStrategyFactionRowColor,
  ATTACK_STRATEGY_PANEL_BOX_STYLE,
} from '../utils/attackStrategyStatistics';
import './AttackStrategyStatisticsPanel.css';

const MAX_BAR_HEIGHT = 60;

/**
 * War Effort attack-strategy statistics panels (by rarity, faction, cost, abilities).
 * @param {object[]} orders - One entry per strategy slot (duplicates included).
 * @param {'column'|'row'} layout - Panel stack direction.
 */
function AttackStrategyStatisticsPanel({ orders, layout = 'column' }) {
  const stats = useMemo(() => computeAttackStrategyStatistics(orders), [orders]);
  const {
    costCounts,
    rarityCounts,
    factionEntries,
    abilityEntries,
    units,
    drones,
    weapons,
    stratagems,
    maxCostCount,
    maxRarityCount,
    RARITIES,
  } = stats;

  const layoutClass =
    layout === 'row'
      ? 'attack-strategy-statistics-layout attack-strategy-statistics-layout--row'
      : 'attack-strategy-statistics-layout';

  return (
    <div className={layoutClass}>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-rarity-graph-wrapper">
          <div className="attack-strategy-rarity-title">By rarity</div>
          <div className="attack-strategy-rarity-bar-track">
            <div className="attack-strategy-rarity-bar-container">
              {RARITIES.map((rarity) => {
                const count = rarityCounts[rarity] || 0;
                const barHeight = maxRarityCount > 0 ? (count / maxRarityCount) * MAX_BAR_HEIGHT : 0;
                return (
                  <div key={rarity} className={`attack-strategy-rarity-bar-segment rarity-${rarity}`}>
                    <div
                      className="attack-strategy-rarity-bar-fill"
                      style={{ height: `${barHeight}px`, minHeight: count > 0 ? '4px' : 0 }}
                      title={`${rarity}: ${count} orders`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <div className="attack-strategy-rarity-labels-row">
            {RARITIES.map((r) => (
              <span key={r} className={`attack-strategy-rarity-bar-label rarity-${r}`}>
                {r}
              </span>
            ))}
          </div>
          <div className="attack-strategy-rarity-counts-row">
            {RARITIES.map((r) => (
              <span key={r} className="attack-strategy-rarity-count">
                {rarityCounts[r] || 0}
              </span>
            ))}
          </div>
        </div>
        <div className="attack-strategy-faction-counts-wrapper">
          <div className="attack-strategy-rarity-title">By faction</div>
          <div className="attack-strategy-faction-counts-list">
            {factionEntries.map(([factionName, count]) => (
              <div
                key={factionName}
                className="attack-strategy-faction-count-row"
                style={{ borderLeftColor: getAttackStrategyFactionRowColor(factionName) }}
              >
                <span className="attack-strategy-faction-name">{factionName}</span>
                <span className="attack-strategy-faction-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-cost-graph-wrapper">
          <div className="attack-strategy-cost-title">By cost</div>
          <div className="attack-strategy-cost-bar-container">
            {[...Array(13).keys()].map((cost) => {
              const count = costCounts[cost];
              const barHeight = maxCostCount > 0 ? (count / maxCostCount) * MAX_BAR_HEIGHT : 0;
              return (
                <div key={cost} className="attack-strategy-cost-bar-segment">
                  <div
                    className="attack-strategy-cost-bar-fill"
                    style={{ height: `${barHeight}px` }}
                    title={`Cost ${cost}: ${count} orders`}
                  />
                  <span className="attack-strategy-cost-bar-label">{cost}</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="attack-strategy-type-counters">
          <span>Units: {units}</span>
          <span>Drones: {drones}</span>
          <span>Weapons: {weapons}</span>
          <span>Stratagems: {stratagems}</span>
        </div>
      </div>
      <div className="attack-strategy-right-panel" style={ATTACK_STRATEGY_PANEL_BOX_STYLE}>
        <div className="attack-strategy-ability-counts-wrapper">
          <div className="attack-strategy-cost-title">Orders with abilities</div>
          <div className="attack-strategy-ability-counts-list">
            {abilityEntries.length === 0 ? (
              <div className="attack-strategy-ability-count-row">
                <span className="attack-strategy-ability-name">—</span>
                <span className="attack-strategy-ability-count">None</span>
              </div>
            ) : (
              abilityEntries.map(([abilityName, count]) => (
                <div key={abilityName} className="attack-strategy-ability-count-row">
                  <span className="attack-strategy-ability-name">{abilityName}</span>
                  <span className="attack-strategy-ability-count">{count}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AttackStrategyStatisticsPanel;
